// src/components/advisor/AdvisorIntakePanel.js
//
// Ticket 071 (advisor reader). The "Shared intake" panel on the advisor's subject page
// (CaseBriefPage). Presentational and read-only: it draws the allow-listed `intake` view
// that get-advisor-subject returns after its firm and consent checks. No policy here:
// whether this advisor may see the intake, and which saved version, was decided on the
// server (loadReadableIntake). The panel never fetches.
//
// Sections: saved line / contact, "Not provided by the client" (three groups), the
// client's answers, and what the engine produced. Facts (answers) and conclusions
// (results) stay in separate sections. Wording comes from the client's own intake
// config through src/lib/advisorIntakeModel.js; the 1900 move-date placeholder prints as
// "Lived here since before {year}", never as a date.
//
// Shared pieces reused, not copied: IrpfEstimateBody, AutonomoSummaryBlock,
// SnapshotFinding, SnapshotTimeline, buildSnapshotFindings.

import React, { useMemo } from 'react'
import { IrpfEstimateBody } from '../calculator/IrpfResultPanels'
import { AutonomoSummaryBlock } from '../calculator/AutonomoSummaryBlock'
import SnapshotFinding from '../SnapshotFinding'
import SnapshotTimeline from '../SnapshotTimeline'
import buildSnapshotFindings from '../../lib/buildSnapshotFindings'
import { RESULTS_RATE_YEAR_LINE } from '../../lib/intake/intakeConfig'
import {
  buildAdvisorIntakeModel, ADVISOR_INTAKE_TITLE, ADVISOR_INTAKE_CONTACT_TITLE, ADVISOR_INTAKE_GAPS_TITLE,
  ADVISOR_INTAKE_GAPS_NONE, ADVISOR_INTAKE_GAP_GROUPS, ADVISOR_INTAKE_ANSWERS_TITLE, ADVISOR_INTAKE_RESULTS_TITLE,
  ADVISOR_INTAKE_RESULTS_NOTE, ADVISOR_INTAKE_ESTIMATE_HEADING, ADVISOR_INTAKE_SNAPSHOT_HEADING,
  ADVISOR_INTAKE_AUTONOMO_HEADING, ADVISOR_INTAKE_NO_ESTIMATE, ADVISOR_INTAKE_STANDOUT_HEADING, ADVISOR_INTAKE_FINDINGS_STILL_APPLY,
} from '../../lib/advisorIntakeModel'

function GapRow({ label, detail }) {
  return (
    <li className="brief-intake-row">
      <span className="brief-intake-label">{label}</span>
      {detail && <span className="u-hint">{detail}</span>}
    </li>
  )
}

function GapGroup({ title, children, count }) {
  if (count === 0) return null
  return (
    <div className="brief-intake-group">
      <h3 className="brief-intake-subheading">{title}</h3>
      <ul className="brief-intake-list">{children}</ul>
    </div>
  )
}

function Estimate({ results, estimate }) {
  if (estimate.kind === 'estimate') {
    return (
      <section className="intake-results-block">
        <h3 className="brief-intake-subheading">{ADVISOR_INTAKE_ESTIMATE_HEADING}</h3>
        <IrpfEstimateBody result={results.irpf.result} filingComparison={results.irpf.filing_comparison} />
      </section>
    )
  }
  if (estimate.kind === 'blocked') {
    return (
      <section className="intake-results-block">
        <h3 className="brief-intake-subheading">{ADVISOR_INTAKE_NO_ESTIMATE}</h3>
        {estimate.reasons.map((reason) => <p className="cal-blocked-reason" key={reason}>{reason}</p>)}
        <p className="u-hint">{ADVISOR_INTAKE_FINDINGS_STILL_APPLY}</p>
      </section>
    )
  }
  return (
    <section className="intake-results-block">
      <h3 className="brief-intake-subheading">{ADVISOR_INTAKE_NO_ESTIMATE}</h3>
    </section>
  )
}

function Snapshot({ results }) {
  const snapshot = results.snapshot || {}
  const facts = snapshot.derived_facts || {}
  const flags = snapshot.flags || []
  const taxYear = facts.taxYear || (results.meta && results.meta.facts_tax_year)
  const { findings, notableChips, prose, timelineNodes } = buildSnapshotFindings(facts, flags, taxYear)
  return (
    <section className="intake-results-block">
      <h3 className="brief-intake-subheading">{ADVISOR_INTAKE_SNAPSHOT_HEADING}</h3>
      <div className="trc-standout">
        <div className="trc-standout-eyebrow">
          <span className="trc-standout-icon">✦</span>
          <div className="u-eyebrow">{ADVISOR_INTAKE_STANDOUT_HEADING}</div>
        </div>
        {notableChips ? (
          <div className="trc-standout-chips">
            {notableChips.map((chip) => <span className="trc-standout-chip" key={chip}>{chip}</span>)}
          </div>
        ) : (
          <p className="trc-standout-prose">{prose}</p>
        )}
      </div>
      <div className="trc-findings-card">
        {findings.map((finding, i) => (
          <SnapshotFinding key={finding.id} index={i + 1} finding={finding} openLinksInNewTab />
        ))}
      </div>
      {timelineNodes.length > 0 && <SnapshotTimeline nodes={timelineNodes} taxYear={taxYear} />}
    </section>
  )
}

// props: intake (the server view), email, phone, showContact (false when the page header
// already shows them).
export default function AdvisorIntakePanel({ intake, email = null, phone = null, showContact = true }) {
  const model = useMemo(() => buildAdvisorIntakeModel(intake), [intake])
  const { gaps, view } = model
  const results = intake.results || {}
  const rateYear = view.rateYear

  return (
    <section className="brief-section brief-intake" aria-label={ADVISOR_INTAKE_TITLE}>
      <div className="brief-section-header">
        <h2 className="brief-section-title">{ADVISOR_INTAKE_TITLE}</h2>
      </div>
      <div className="brief-section-body brief-intake-body">
        <div className="brief-intake-meta">
          <p className="brief-intake-saved">{model.savedLine} <span className="brief-intake-year">· Tax year {model.taxYear}</span></p>
          <p className="u-hint">{model.estimateLine}</p>
          {model.engineLine && <p className="u-hint">{model.engineLine}</p>}
        </div>

        {showContact && (
          <div className="brief-intake-block">
            <h3 className="brief-intake-subheading">{ADVISOR_INTAKE_CONTACT_TITLE}</h3>
            <div className="u-row-md">
              {email && <a className="brief-intake-contact" href={`mailto:${email}`}>{email}</a>}
              {phone && <a className="brief-intake-contact" href={`tel:${phone}`}>{phone}</a>}
              {!email && !phone && <span className="u-hint">No contact details</span>}
            </div>
          </div>
        )}

        <div className="brief-intake-block intake-not-assessed">
          <h3 className="brief-intake-subheading">{ADVISOR_INTAKE_GAPS_TITLE}</h3>
          {!model.hasGaps && <p className="u-hint">{ADVISOR_INTAKE_GAPS_NONE}</p>}
          <GapGroup title={ADVISOR_INTAKE_GAP_GROUPS.skipped} count={gaps.skipped.length}>
            {gaps.skipped.map((r) => <GapRow key={r.id} label={r.question} detail={r.answerText} />)}
          </GapGroup>
          <GapGroup title={ADVISOR_INTAKE_GAP_GROUPS.notSure} count={gaps.notSure.length}>
            {gaps.notSure.map((r) => <GapRow key={r.id} label={r.question} />)}
          </GapGroup>
          <GapGroup title={ADVISOR_INTAKE_GAP_GROUPS.notAssessed} count={gaps.notAssessed.length}>
            {gaps.notAssessed.map((l) => <GapRow key={l.screen} label={l.label} detail={l.impact} />)}
          </GapGroup>
        </div>

        <div className="brief-intake-block">
          <h3 className="brief-intake-subheading">{ADVISOR_INTAKE_ANSWERS_TITLE}</h3>
          {model.sections.map((s) => (
            <div className="brief-intake-group" key={s.section}>
              <h4 className="brief-intake-subheading brief-intake-subheading--minor">{s.title}</h4>
              <ul className="brief-intake-list">
                {s.rows.map((r) => (
                  <li className="brief-intake-row" key={r.id}>
                    <span className="brief-intake-label">{r.question}</span>
                    <span className={`intake-review-answer intake-status--${r.status}`}>{r.answerText}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="brief-intake-block intake-results">
          <h3 className="brief-intake-subheading">{ADVISOR_INTAKE_RESULTS_TITLE}</h3>
          <p className="u-hint">
            {ADVISOR_INTAKE_RESULTS_NOTE}
            {rateYear ? ` ${RESULTS_RATE_YEAR_LINE(rateYear)}` : ''}
          </p>
          <Snapshot results={results} />
          {view.hasAutonomo && results.irpf && results.autonomo && (
            <section className="intake-results-block">
              <h3 className="brief-intake-subheading">{ADVISOR_INTAKE_AUTONOMO_HEADING}</h3>
              <AutonomoSummaryBlock
                autonomoResult={results.autonomo.result}
                autonomoIrpfImpact={results.autonomo.irpf_impact}
                result={results.irpf.result}
                hasPersonalFacts
              />
            </section>
          )}
          <Estimate results={results} estimate={view.estimate} />
        </div>
      </div>
    </section>
  )
}
